import { supabase } from './supabase.js';

/**
 * Each domain table (clients, library, invoices, contacts, activity) stores rows of:
 *   { id (uuid pk = data.id), user_id, data jsonb, created_at }
 * The full domain object lives inside `data`. Reads return `data` so the existing
 * UI code keeps working with the same shape it had under localStorage.
 *
 * `activity` is special — it has top-level `kind` and `text` columns (see schema).
 */

async function userId() {
  const { data } = await supabase.auth.getUser();
  return data.user?.id;
}

export const db = {
  async listJson(table) {
    const { data, error } = await supabase
      .from(table)
      .select('id, data, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(r => r.data);
  },

  async insertJson(table, obj) {
    const uid = await userId();
    if (!uid) throw new Error('Not authenticated');
    const { error } = await supabase
      .from(table)
      .insert({ id: obj.id, user_id: uid, data: obj });
    if (error) throw error;
  },

  async upsertJson(table, obj) {
    const uid = await userId();
    if (!uid) throw new Error('Not authenticated');
    const { error } = await supabase
      .from(table)
      .upsert({ id: obj.id, user_id: uid, data: obj, updated_at: new Date().toISOString() });
    if (error) throw error;
  },

  async deleteRow(table, id) {
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) throw error;
  },

  async listActivity(limit = 50) {
    const { data, error } = await supabase
      .from('activity')
      .select('id, kind, text, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []).map(r => ({ id: r.id, kind: r.kind, text: r.text, ts: new Date(r.created_at).getTime() }));
  },

  async insertActivity(kind, text) {
    const uid = await userId();
    if (!uid) return;
    await supabase.from('activity').insert({ user_id: uid, kind, text });
  },

  async getProfile() {
    const uid = await userId();
    if (!uid) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', uid)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async usageThisPeriod(profile) {
    const uid = await userId();
    if (!uid) return 0;
    let q = supabase.from('generations').select('id', { count: 'exact', head: true }).eq('user_id', uid);
    // Trial: count lifetime. Paid: count from current_period_start.
    if (profile?.plan && profile.plan !== 'trial' && profile.current_period_start) {
      q = q.gte('created_at', profile.current_period_start);
    }
    const { count, error } = await q;
    if (error) throw error;
    return count || 0;
  },

  async wipeAllUserData() {
    // Delete all rows the user owns from domain tables. RLS scopes this to the user.
    for (const t of ['clients', 'library', 'invoices', 'contacts', 'activity', 'prospects', 'outreach_queue', 'outreach_templates']) {
      const uid = await userId();
      if (!uid) return;
      await supabase.from(t).delete().eq('user_id', uid);
    }
  },

  /* ---------------- Prospects ---------------- */
  async listProspects() {
    const { data, error } = await supabase
      .from('prospects')
      .select('id, status, platform, handle, email, observation, data, created_at, updated_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(r => ({
      ...(r.data || {}),
      id: r.id,
      status: r.status,
      platform: r.platform ?? r.data?.platform,
      handle: r.handle ?? r.data?.handle,
      email: r.email ?? r.data?.email,
      observation: r.observation ?? r.data?.observation,
      ts: new Date(r.created_at).getTime()
    }));
  },
  async insertProspect(p) {
    const uid = await userId();
    if (!uid) throw new Error('Not authenticated');
    const { error } = await supabase.from('prospects').insert({
      id: p.id,
      user_id: uid,
      data: p.data || {},
      status: p.status || 'found',
      platform: p.platform ?? p.data?.platform ?? null,
      handle: p.handle ?? p.data?.handle ?? null,
      email: p.email ?? p.data?.email ?? null,
      observation: p.observation ?? p.data?.observation ?? null
    });
    if (error) throw error;
  },
  async updateProspect(id, patch) {
    const { error } = await supabase.from('prospects')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },
  async deleteProspect(id) {
    const { error } = await supabase.from('prospects').delete().eq('id', id);
    if (error) throw error;
  },

  /* ---------------- Outreach queue ---------------- */
  async listQueue() {
    const { data, error } = await supabase
      .from('outreach_queue')
      .select('id, prospect_id, data, status, platform, subject, sent_at, follow_up_due, reply_logged, reply_text, notes, converted_to_client, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(r => ({
      ...(r.data || {}),
      id: r.id,
      prospectId: r.prospect_id,
      status: r.status,
      messageType: r.platform ?? r.data?.messageType,   // back-compat alias
      platform: r.platform ?? r.data?.messageType,
      subject: r.subject ?? r.data?.subject,
      notes: r.notes,
      replyLogged: r.reply_logged,
      replyText: r.reply_text,
      followUpDue: r.follow_up_due ? new Date(r.follow_up_due).getTime() : null,
      convertedClientId: r.converted_to_client,
      sentAt: r.sent_at ? new Date(r.sent_at).getTime() : null,
      ts: new Date(r.created_at).getTime()
    }));
  },
  async insertQueue(q) {
    const uid = await userId();
    if (!uid) throw new Error('Not authenticated');
    const { error } = await supabase.from('outreach_queue').insert({
      id: q.id,
      user_id: uid,
      prospect_id: q.prospectId || null,
      data: q.data || {},
      status: q.status || 'drafting',
      platform: q.platform ?? q.data?.messageType ?? null,
      subject: q.subject ?? q.data?.subject ?? null,
      sent_at: q.sentAt ? new Date(q.sentAt).toISOString() : null
    });
    if (error) throw error;
  },
  async updateQueue(id, patch) {
    const upd = { ...patch, updated_at: new Date().toISOString() };
    if (patch.sentAt !== undefined) { upd.sent_at = patch.sentAt ? new Date(patch.sentAt).toISOString() : null; delete upd.sentAt; }
    if (patch.followUpDue !== undefined) { upd.follow_up_due = patch.followUpDue ? new Date(patch.followUpDue).toISOString() : null; delete upd.followUpDue; }
    if (patch.replyLogged !== undefined) { upd.reply_logged = patch.replyLogged; delete upd.replyLogged; }
    if (patch.replyText !== undefined) { upd.reply_text = patch.replyText; delete upd.replyText; }
    if (patch.convertedClientId !== undefined) { upd.converted_to_client = patch.convertedClientId; delete upd.convertedClientId; }
    if (patch.platform !== undefined) { /* keep */ }
    const { error } = await supabase.from('outreach_queue').update(upd).eq('id', id);
    if (error) throw error;
  },
  async deleteQueue(id) {
    const { error } = await supabase.from('outreach_queue').delete().eq('id', id);
    if (error) throw error;
  },

  /* ---------------- Templates ---------------- */
  async listTemplates() {
    const { data, error } = await supabase
      .from('outreach_templates')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(r => ({
      id: r.id, name: r.name, platform: r.platform, niche: r.niche,
      type: r.type, subject: r.subject, body: r.body,
      ts: new Date(r.created_at).getTime()
    }));
  },
  async insertTemplate(t) {
    const uid = await userId();
    if (!uid) throw new Error('Not authenticated');
    const { error, data } = await supabase.from('outreach_templates').insert({
      user_id: uid,
      name: t.name, platform: t.platform || null, niche: t.niche || null,
      type: t.type || null, subject: t.subject || null, body: t.body
    }).select().maybeSingle();
    if (error) throw error;
    return data;
  },
  async deleteTemplate(id) {
    const { error } = await supabase.from('outreach_templates').delete().eq('id', id);
    if (error) throw error;
  }
};
